import React, { useState } from 'react';
import type { ImageListType } from 'react-images-uploading';
import { ImageUploader } from './ImageUploader';
import Select from 'react-select';
import type { ValueType } from 'react-select';
import Button, { ButtonType } from '../../shared/components/core-ui/button/button.component';
import styles from '../furnishingContainer.module.scss';
import type { PortfolioProject, PortfolioRoom } from './portfolio.types';
import { createPortfolioRoom } from '../furnishing.service';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';

interface PortfolioRoomFormProps {
  portfolios: PortfolioProject[];
  onRoomAdded: (portfolioId: string, room: PortfolioRoom) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const generateId = () => `room_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export const PortfolioRoomForm: React.FC<PortfolioRoomFormProps> = ({
  portfolios,
  onRoomAdded
}) => {
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [roomName, setRoomName] = useState('');
  const [roomOrder, setRoomOrder] = useState<number>(0);
  const [roomImages, setRoomImages] = useState<Array<{ dataURL?: string; file?: File }>>([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const portfolioOptions: SelectOption[] = portfolios.map((p) => ({
    value: p.id,
    label: `${p.projectName} - ${p.location}`
  }));

  const handlePortfolioChange = (selectedOption: ValueType<SelectOption, false>): void => {
    if (selectedOption) {
      setSelectedPortfolioId((selectedOption as SelectOption).value);
      setError('');
    }
  };

  const handleImageChange = (imageList: ImageListType) => {
    setRoomImages(imageList as Array<{ dataURL?: string; file?: File }>);
  };

  const handleSubmitRoom = async () => {
    if (!selectedPortfolioId) {
      setError('Please select a portfolio first.');
      return;
    }

    if (!roomName.trim()) {
      setError('Please enter a room name.');
      return;
    }

    if (roomImages.length === 0) {
      setError('Please add at least one image to the room.');
      return;
    }

    const imageFiles = roomImages.filter((img) => img.file).map((img) => img.file as File);

    if (imageFiles.length === 0) {
      setError('Please upload at least one image file.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await createPortfolioRoom({
        portfolioId: selectedPortfolioId,
        name: roomName.trim(),
        assets: imageFiles,
        order: roomOrder
      });

      if (isErrorInfo(result)) {
        setError(result.errorMessage || 'Failed to create room. Please try again.');
        return;
      }

      const portfolioRoom: PortfolioRoom = {
        id: result.id || generateId(),
        name: result.name || roomName.trim(),
        images: result.images || roomImages.map((img) => img.dataURL || ''),
        order: result.order || roomOrder
      };

      onRoomAdded(selectedPortfolioId, portfolioRoom);
      resetForm();
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setRoomName('');
    setRoomOrder(0);
    setRoomImages([]);
    setError('');
  };

  const selectedPortfolio = portfolios.find((p) => p.id === selectedPortfolioId);

  if (portfolios.length === 0) return null;

  return (
    <div
      className={styles['furnishing-management__form-card']}
      style={{
        marginTop: '1rem'
      }}
    >
      <div className={styles['furnishing-management__section-header']}>
        <h2>Add Room to Portfolio</h2>
      </div>

      {/* Portfolio Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <div className={styles['furnishing-management__input']}>
          <span>Select Portfolio Project *</span>
          <div className={styles['furnishing-management__select']}>
            <Select
              classNamePrefix="react-select"
              placeholder="Select a portfolio..."
              options={portfolioOptions}
              value={portfolioOptions.find((opt) => opt.value === selectedPortfolioId) || null}
              onChange={handlePortfolioChange}
            />
          </div>
        </div>

        {selectedPortfolio && (
          <div
            style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#f8f6f1',
              borderRadius: '8px',
              border: '1px solid #e0d8ca'
            }}
          >
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.9rem' }}>
              <div>
                <strong>Location:</strong> {selectedPortfolio.location}
              </div>
              <div>
                <strong>Home Type:</strong> {selectedPortfolio.homeType}
              </div>
              <div>
                <strong>Existing Rooms:</strong> {selectedPortfolio.rooms.length}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Room Form Section */}
      {selectedPortfolioId && (
        <div className={styles['furnishing-management__models-section']}>
          <div className={styles['furnishing-management__section-header']}>
            <h3>Room Details</h3>
          </div>

          {/* Room Name Input */}
          <div
            className={styles['furnishing-management__form-grid']}
            style={{ marginBottom: '1.5rem' }}
          >
            <div className={styles['furnishing-management__input']}>
              <span>Room Name *</span>
              <input
                type="text"
                placeholder="Enter room name (e.g., Master Bedroom, Living Room)"
                value={roomName}
                onChange={(e) => {
                  setRoomName(e.target.value);
                  setError('');
                }}
              />
            </div>

            <div className={styles['furnishing-management__input']}>
              <span>Room Order</span>
              <input
                type="number"
                placeholder="Enter display order (optional)"
                value={roomOrder}
                onChange={(e) => {
                  setRoomOrder(Number(e.target.value) || 0);
                  setError('');
                }}
                min="0"
              />
            </div>
          </div>

          {/* Room Image Upload Area */}
          <div
            className={styles['furnishing-management__model']}
            style={{ background: '#fff', border: '1px solid #e0d8ca' }}
          >
            <div
              style={{
                marginBottom: '1rem',
                paddingBottom: '0.5rem',
                borderBottom: '1px solid #eee'
              }}
            >
              <span style={{ fontWeight: 'bold', color: '#566346' }}>
                Room Images * {roomImages.length > 0 && `(${roomImages.length} selected)`}
              </span>
            </div>

            <ImageUploader
              images={roomImages}
              onImageChange={handleImageChange}
              maxNumber={10}
              multiple
              altText={roomName || 'Room'}
            />
          </div>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '1rem',
            background: '#ffebee',
            color: '#c62828',
            borderRadius: '8px',
            marginTop: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: '500'
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>⚠️</span> {error}
        </div>
      )}

      {selectedPortfolioId && (
        <div
          className={styles['furnishing-management__actions']}
          style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #eee' }}
        >
          <Button type={ButtonType.gray} onClick={resetForm} disabled={isSubmitting}>
            Clear Form
          </Button>
          <Button onClick={handleSubmitRoom} type={ButtonType.primary} disabled={isSubmitting}>
            {isSubmitting ? 'Adding...' : 'Add Room to Portfolio'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PortfolioRoomForm;
